import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  getTheory,
  listComments,
  postComment,
  voteCredibility,
} from "@/api";
import { useAuth } from "@/auth";
import { THEORY } from "@/constants/testIds";
import CredibilityBar from "@/components/CredibilityBar";
import CredibilityTrend from "@/components/CredibilityTrend";
import AsciiDivider from "@/components/AsciiDivider";
import CommentList from "@/components/CommentNode";
import EvidenceSection from "@/components/EvidenceSection";

const TheoryDetail = () => {
  const { slug } = useParams();
  const { user, refresh } = useAuth();
  const [theory, setTheory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const [sliderValue, setSliderValue] = useState(50);
  const [voteBusy, setVoteBusy] = useState(false);

  const [comments, setComments] = useState([]);
  const [sort, setSort] = useState("hot");
  const [commentText, setCommentText] = useState("");
  const [postBusy, setPostBusy] = useState(false);

  const loadTheory = useCallback(async () => {
    try {
      const t = await getTheory(slug);
      setTheory(t);
      if (t.user_vote != null) setSliderValue(t.user_vote);
      else if (t.community_credibility != null)
        setSliderValue(Math.round(t.community_credibility));
      else setSliderValue(t.base_credibility);
    } catch (e) {
      setErr(
        typeof e?.response?.data?.detail === "string"
          ? e.response.data.detail
          : "Could not load dossier."
      );
    } finally {
      setLoading(false);
    }
  }, [slug]);

  const loadComments = useCallback(async () => {
    try {
      const list = await listComments(slug, sort);
      setComments(list);
    } catch {
      /* ignore */
    }
  }, [slug, sort]);

  useEffect(() => {
    loadTheory();
  }, [loadTheory]);

  useEffect(() => {
    if (theory && !theory.paywall) loadComments();
  }, [loadComments, theory]);

  const submitCredibility = async () => {
    setVoteBusy(true);
    try {
      const data = await voteCredibility(slug, sliderValue);
      setTheory((prev) => ({
        ...prev,
        community_credibility: data.community_credibility,
        community_vote_count: data.community_vote_count,
        user_vote: data.user_vote,
      }));
    } finally {
      setVoteBusy(false);
    }
  };

  const submitComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim()) return;
    setPostBusy(true);
    try {
      await postComment(slug, commentText.trim());
      setCommentText("");
      await loadComments();
    } finally {
      setPostBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="term-container">
        <p className="muted">$ loading dossier <span className="cursor" /></p>
      </div>
    );
  }

  if (err) {
    return (
      <div className="term-container">
        <p style={{ color: "var(--ax-danger)" }}>{err}</p>
      </div>
    );
  }

  if (!theory) {
    return (
      <div className="term-container">
        <p className="muted">Dossier not found.</p>
      </div>
    );
  }

  const cred =
    theory.community_credibility != null
      ? theory.community_credibility
      : theory.base_credibility;

  return (
    <div className="term-container fade-in" data-testid={THEORY.root}>
      <div className="kbd-label">// dossier · {slug}</div>
      <h1
        data-testid={THEORY.title}
        style={{
          margin: "0.2rem 0 1rem",
          fontSize: "clamp(1.6rem, 4vw, 2.4rem)",
          letterSpacing: "0.1em",
          color: "var(--ax-accent)",
        }}
      >
        {theory.title}
      </h1>

      <CredibilityBar
        testId={THEORY.credibilityBar}
        score={cred}
        label={
          theory.community_credibility != null ? "COMMUNITY" : "BASELINE"
        }
      />
      <p className="faint" style={{ fontSize: 12, marginTop: "0.3rem" }}>
        {theory.community_vote_count} community votes · baseline editorial
        score: {theory.base_credibility}/100
      </p>
      <CredibilityTrend slug={slug} days={30} />

      <AsciiDivider />

      <section style={{ margin: "1.5rem 0" }} data-testid={THEORY.narrative}>
            <div className="kbd-label">$ cat /narratives/official.txt</div>
            <div className="term-card">
              <div className="muted" style={{ fontSize: 11, marginBottom: 4 }}>
                OFFICIAL NARRATIVE
              </div>
              <div>{theory.official_narrative}</div>
            </div>
          </section>

          <section
            style={{ margin: "1.5rem 0" }}
            data-testid={THEORY.counterEvidence}
          >
            <div className="kbd-label">{`$ grep -r "counter" /evidence/`}</div>
            <div className="term-card">
              <div
                style={{
                  color: "var(--ax-warn)",
                  fontSize: 11,
                  marginBottom: 6,
                  letterSpacing: "0.1em",
                }}
              >
                COUNTER-EVIDENCE
              </div>
              <ul style={{ paddingLeft: "1.1rem", margin: 0 }}>
                {theory.counter_evidence.map((c, i) => (
                  <li key={`ce-${i}-${c.slice(0, 32)}`} style={{ marginBottom: "0.45rem" }}>
                    {c}
                  </li>
                ))}
              </ul>
            </div>
          </section>

          <section
            style={{ margin: "1.5rem 0" }}
            data-testid={THEORY.supportingEvidence}
          >
            <div className="kbd-label">{`$ grep -r "support" /evidence/`}</div>
            <div className="term-card">
              <div
                style={{
                  color: "var(--ax-accent)",
                  fontSize: 11,
                  marginBottom: 6,
                  letterSpacing: "0.1em",
                }}
              >
                SUPPORTING EVIDENCE
              </div>
              <ul style={{ paddingLeft: "1.1rem", margin: 0 }}>
                {theory.supporting_evidence.map((c, i) => (
                  <li key={`se-${i}-${c.slice(0, 32)}`} style={{ marginBottom: "0.45rem" }}>
                    {c}
                  </li>
                ))}
              </ul>
            </div>
          </section>

          <section style={{ margin: "1.5rem 0" }} data-testid={THEORY.keyFigures}>
            <div className="kbd-label">$ ls /actors/</div>
            <div className="term-card">
              <div className="muted" style={{ fontSize: 11, marginBottom: 6 }}>
                KEY FIGURES
              </div>
              <ul
                style={{
                  listStyle: "none",
                  padding: 0,
                  margin: 0,
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill,minmax(220px,1fr))",
                  gap: "0.4rem",
                }}
              >
                {theory.key_figures.map((k, i) => (
                  <li key={`kf-${i}-${k.slice(0, 32)}`} className="muted">
                    {">"} {k}
                  </li>
                ))}
              </ul>
            </div>
          </section>

          <AsciiDivider char="=" />

          <EvidenceSection slug={slug} user={user} />

          <AsciiDivider char="=" />

          <section style={{ margin: "1.5rem 0" }}>
            <div className="kbd-label">$ vote --credibility</div>
            <div className="term-card">
              <div className="muted" style={{ fontSize: 12 }}>
                Your assessment of this dossier (1 = entirely false, 100 =
                entirely true).
              </div>
              <input
                type="range"
                min={1}
                max={100}
                value={sliderValue}
                onChange={(e) => setSliderValue(parseInt(e.target.value, 10))}
                data-testid={THEORY.credibilitySlider}
                style={{ width: "100%", marginTop: "0.6rem", accentColor: "var(--ax-accent)" }}
              />
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginTop: "0.4rem",
                }}
              >
                <span className="glow" style={{ fontWeight: 700 }}>
                  {sliderValue}/100
                </span>
                <button
                  data-testid={THEORY.credibilitySubmit}
                  onClick={submitCredibility}
                  disabled={voteBusy}
                >
                  {voteBusy
                    ? "SUBMITTING…"
                    : theory.user_vote != null
                    ? "UPDATE VOTE"
                    : "CAST VOTE"}
                </button>
              </div>
              {theory.user_vote != null && (
                <div className="faint" style={{ fontSize: 11, marginTop: 6 }}>
                  Your last vote: {theory.user_vote}/100
                </div>
              )}
            </div>
          </section>

          <AsciiDivider />

          <section style={{ margin: "1.5rem 0" }}>
            <div className="kbd-label">$ tail -f /comments/</div>
            <div className="tab-bar">
              <button
                data-testid={THEORY.sortHot}
                className={sort === "hot" ? "active" : ""}
                onClick={() => setSort("hot")}
              >
                hot
              </button>
              <button
                data-testid={THEORY.sortNew}
                className={sort === "new" ? "active" : ""}
                onClick={() => setSort("new")}
              >
                new
              </button>
              <button
                data-testid={THEORY.sortTop}
                className={sort === "top" ? "active" : ""}
                onClick={() => setSort("top")}
              >
                top
              </button>
            </div>

            <form onSubmit={submitComment} style={{ marginBottom: "1rem" }}>
              <textarea
                data-testid={THEORY.commentInput}
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                placeholder={`$ as ${user?.codename || "anonymous"}: post your dossier annotation…`}
                rows={3}
              />
              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "0.4rem" }}>
                <button
                  data-testid={THEORY.commentSubmit}
                  type="submit"
                  disabled={postBusy || !commentText.trim()}
                >
                  {postBusy ? "POSTING…" : "POST"}
                </button>
              </div>
            </form>

            <div data-testid={THEORY.commentList}>
              {comments.length === 0 && (
                <p className="muted">
                  $ no comments yet. be the first to annotate this dossier.
                </p>
              )}
              <CommentList tree={comments} slug={slug} onUpdate={loadComments} />
            </div>
          </section>
    </div>
  );
};

export default TheoryDetail;
;
